import type { Type } from '@angular/core'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as data from '../../data'

import type { CssConfigurable } from './css-configurable'
import type { Row } from './row'

/**
 * Defines configuration options and settings for individual table cells. This
 * interface handles the display, behavior, and interaction properties of a
 * cell.
 */
export interface Cell<TComponentType extends Type<any> = Type<any>>
  extends CssConfigurable.Optionally {
  // TODO: Fix typing for inputs and outputs, add more constraints to them. At
  // this point typing will pick all props from Instance?
  inputs?: Partial<InstanceType<TComponentType>>
  // TODO: We need to do a mapped type to convey that we need callback for each
  // output property, and we will call those callbacks under the hood, when
  // corresponding output is submitted
  outputs?: Partial<InstanceType<TComponentType>>
  /**
   * The component to be used to render the cell.
   *
   * @default components.cellValue.Component
   */
  component?: TComponentType
  /**
   * On cell click callback handler.
   *
   * When provided, cell will be clickable.
   *
   */
  onClick?: () => void
}
export namespace Cell {
  /** TODO: Explain */
  export type Factory<
    TTypeParams extends ec.models.TypeParams.Constraint,
    TComponentType extends Type<any> = Type<any>,
  > = (context: Context<TTypeParams>) => Cell<TComponentType>
  export namespace Factory {
    /**
     * A cell config factory that may return `undefined` to signal that the
     * {@link DEFAULT} factory should be used instead.
     */
    export type Optional<
      TTypeParams extends ec.models.TypeParams.Constraint,
      TComponentType extends Type<any> = Type<any>,
    > = (
      context: Context<TTypeParams>,
    ) => Cell<TComponentType> | undefined | void
    export const DEFAULT: Factory<
      ec.models.TypeParams.Constraint.Default
    > = cellConfig => ({
      component: data.cell.value.Component,
      // TODO: how to infer 'cellConfig' type here?
      inputs: {
        cellConfig,
      },
    })
  }

  /**
   * Contains information about the cell within its context.
   * Extends {@link Row.Context} since a cell exists within a row.
   */
  export interface Context<TTypeParams extends ec.models.TypeParams.Constraint>
    extends Row.Context<TTypeParams> {
    /** The actual value of this {@link Cell} */
    value: unknown
    /** The {@link ec.models.Attribute attribute} representing this {@link Cell} */
    attribute: ec.models.Attribute<TTypeParams>
  }

  /**
   * A cell is created using a callback mechanism. This type represents
   * the cell after the callback has been applied.
   */
  export type Applied<TTypeParams extends ec.models.TypeParams.Constraint> =
    Required.Semi<Cell, 'component'> & Context<TTypeParams>
}
